/**
 * Flow Queuing System — the per-item send-pipeline lock, extracted verbatim
 * from `EnrollQueueContext.tsx` (F9 god-context cut 3 of 4).
 *
 * Self-contained by construction: nothing in here reads a mutation, a query,
 * or any other provider state — it is a ref/state pair plus three callbacks
 * over it. The mutations block (`addMutation`/`patchMutation`/
 * `removeMutation`/`unstickMutation`/`sendMutation`) stays in the context
 * file, reserved for the F1/DBOS worktree; no part of it was pulled in here.
 *
 * `busyItems` is returned alongside the three context-facing members because
 * the provider's ghost-entry sweep reads the raw set (and re-runs on it) to
 * decide that an item inside an active send pipeline is never "departed".
 */
import { useCallback, useRef, useState } from 'react';
import type { EnrollQueueContextValue } from './enroll-queue-types';

export type SendLock = Pick<EnrollQueueContextValue, 'isItemBusy' | 'beginSendPipeline' | 'endSendPipeline'> & {
  busyItems: ReadonlySet<number>;
};

export function useSendLock(): SendLock {
  /**
   * Adversarial follow-up (findings 1/2/5) — the per-item send-pipeline
   * lock (see `EnrollQueueContextValue.isItemBusy`). Lives HERE because the
   * provider is the only mount that survives every collapse/close: a
   * remounted editor, or a different trigger surface for the same item,
   * must see the same lock. REF = the synchronous check-and-set truth (two
   * clicks landing in one batch can't both pass a state-read guard);
   * STATE = what renders derive field locks and Remove/Close disables from.
   */
  const busyItemsRef = useRef<Set<number>>(new Set());
  const [busyItems, setBusyItems] = useState<ReadonlySet<number>>(() => new Set());

  // ── Per-item send-pipeline lock (adversarial follow-up, findings 1/2/5;
  // see the interface docs). Claim/release go through the REF synchronously
  // — the whole point is that two clicks in one event batch, or two
  // surfaces' handlers, can never both pass the check — and mirror into
  // state so every mounted instance's `locked`/disabled affordances render
  // from it. ────────────────────────────────────────────────────────────────

  const beginSendPipeline = useCallback((itemIds: number[]): boolean => {
    if (itemIds.some(id => busyItemsRef.current.has(id))) return false;
    const next = new Set(busyItemsRef.current);
    for (const id of itemIds) next.add(id);
    busyItemsRef.current = next;
    setBusyItems(next);
    return true;
  }, []);

  const endSendPipeline = useCallback((itemIds: number[]) => {
    const next = new Set(busyItemsRef.current);
    let changed = false;
    for (const id of itemIds) {
      if (next.delete(id)) changed = true;
    }
    if (!changed) return;
    busyItemsRef.current = next;
    setBusyItems(next);
  }, []);

  const isItemBusy = useCallback((itemId: number) => busyItems.has(itemId), [busyItems]);

  return { busyItems, isItemBusy, beginSendPipeline, endSendPipeline };
}
